/**
 * Block deprecation
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-deprecation/
 * @see https://developer.wordpress.org/news/2023/03/block-deprecation-a-tutorial/
 */

import { RichText, useBlockProps, useInnerBlocksProps } from '@wordpress/block-editor';
import LegalCopy from '../block-components/LegalCopy';
import { filterOutWhiteSpace } from '../utils/general-utils';
import {
  circleEnvelopeIcon,
  defaultEnvelopeIcon,
  paperPlaneIcon,
  starEnvelopeIcon
} from '../../img/icons';

const v3 = {
  supports: {
    anchor: true
  },
  attributes: {
    backgroundColor: {
      default: '',
      type: 'string'
    },
    borderStyle: {
      default: '',
      type: 'string'
    },
    borderColor: {
      default: '',
      type: 'string'
    },
    borderWidth: {
      default: '',
      type: 'string'
    },
    image: {
      attribute: 'src',
      default: '',
      selector: 'img',
      source: 'attribute',
      type: 'string'
    },
    alt: {
      attribute: 'alt',
      default: '',
      source: 'attribute',
      type: 'string'
    },
    heading: {
      default: [],
      selector: '.newsletter__heading',
      source: 'children',
      type: 'array'
    },
    headingColor: {
      default: '',
      type: 'string'
    },
    text: {
      default: [],
      selector: '.newsletter__text',
      source: 'children',
      type: 'array'
    },
    textColor: {
      default: '',
      type: 'string'
    },
    legalText: {
      default: '',
      selector: '.newsletter__legal',
      type: 'string'
    },
    legalTextColor: {
      default: '',
      type: 'string'
    },
    emailFieldColor: {
      default: '',
      type: 'string'
    },
    siteIdApi: {
      default: '',
      type: 'string'
    },
    programIdApi: {
      default: '',
      type: 'string'
    },
    preferenceIdApi: {
      default: '',
      type: 'string'
    },
    preferenceKeyApi: {
      default: '',
      type: 'string'
    },
    preferenceTextApi: {
      default: '',
      type: 'string'
    },
    acquisitionCodeApi: {
      default: '',
      type: 'string'
    },
    welcomeEmailApi: {
      default: true,
      type: 'boolean'
    }
  },

  save({ attributes }) {
    const {
      backgroundColor,
      image,
      borderColor,
      borderStyle,
      borderWidth,
      alt,
      heading,
      headingColor,
      text,
      textColor,
      legalText,
      legalTextColor,
      emailFieldColor
    } = attributes;

    const blockProps = useBlockProps.save({
      className: 'block__newsletter'
    });

    const innerBlocksProps = useInnerBlocksProps.save({
      className: 'newsletter__form'
    });

    const newsletterInnerClasses = filterOutWhiteSpace( [
      'newsletter--inner',
      borderStyle,
      borderWidth
    ] );

    const iconMap = {
      'circle-envelope': circleEnvelopeIcon,
      'default-envelope': defaultEnvelopeIcon,
      'star-envelope': starEnvelopeIcon,
      'paper-plane': paperPlaneIcon
    };
    const altText = alt ?? '';
    const outputImageTag = ( () => {
      if ( !image || image === 'Select an icon' ) return null;

      // Check if it's one of our predefined icons
      if ( iconMap[image] ) {
        return iconMap[image];
      }

      // Otherwise it's an uploaded image URL
      return <img src={image} alt={altText} />;
    })();

    const bgColor = backgroundColor ? { backgroundColor } : undefined;
    const bColor = borderColor ? { borderColor } : undefined;

    const hColor = headingColor ? { color: headingColor } : undefined;
    const tColor = textColor ? { color: textColor } : undefined;
    const ltColor = legalTextColor ? { color: legalTextColor } : undefined;
    const efColor = emailFieldColor ? { '--email-field-color': emailFieldColor } : undefined;

    return (
      <div { ...blockProps }>
        <div
          className={ newsletterInnerClasses }
          style={ { ...bgColor, ...bColor, ...efColor } }
        >
          <div className='newsletter-wrapper mx-auto flex flex-col has-success'>

            { image && (
              <div className='newsletter__image-wrapper'>
                { outputImageTag }
              </div>
            ) }

            { heading.length > 0 && (
              <RichText.Content
                className='heading-4 newsletter__heading'
                style={ { ...hColor } }
                tagName='h4'
                value={ heading }
              />
            ) }

            { text.length > 0 && (
              <RichText.Content
                className='paragraph-1 newsletter__text'
                style={ { ...tColor } }
                tagName='p'
                value={ text }
              />
            ) }

            {/* Block email-form */}
            <div { ...innerBlocksProps } />
            { legalText
              ? <RichText.Content
                className='paragraph-3 newsletter__legal'
                style={ { ...tColor } }
                tagName='p'
                value={ legalText }
              />
              : <LegalCopy
                className="paragraph-3 newsletter__legal"
                style={ { ...ltColor } }
              />
            }
          </div>
        </div>
      </div>
    );
  }
};

const v2 = {
  supports: {
    anchor: true
  },
  attributes: {
    backgroundColor: {
      default: '',
      type: 'string'
    },
    borderStyle: {
      default: '',
      type: 'string'
    },
    borderColor: {
      default: '',
      type: 'string'
    },
    borderWidth: {
      default: '',
      type: 'string'
    },
    image: {
      attribute: 'src',
      default: '',
      selector: 'img',
      source: 'attribute',
      type: 'string'
    },
    alt: {
      attribute: 'alt',
      default: '',
      source: 'attribute',
      type: 'string'
    },
    heading: {
      default: [],
      selector: '.newsletter__heading',
      source: 'children',
      type: 'array'
    },
    headingColor: {
      default: '',
      type: 'string'
    },
    text: {
      default: [],
      selector: '.newsletter__text',
      source: 'children',
      type: 'array'
    },
    textColor: {
      default: '',
      type: 'string'
    },
    legalText: {
      default: '',
      selector: '.newsletter__legal',
      type: 'string'
    },
    legalTextColor: {
      default: '',
      type: 'string'
    },
    emailFieldColor: {
      default: '',
      type: 'string'
    }
  },
  save({ attributes }) {
    const {
      backgroundColor,
      borderColor,
      borderStyle,
      borderWidth,
      image,
      alt,
      heading,
      headingColor,
      text,
      textColor,
      legalText,
      legalTextColor,
      emailFieldColor
    } = attributes;

    const blockProps = useBlockProps.save({
      className: 'block__newsletter'
    });

    const innerBlocksProps = useInnerBlocksProps.save({
      className: 'newsletter__form'
    });

    const newsletterInnerClasses = filterOutWhiteSpace( [
      'newsletter--inner',
      borderStyle,
      borderWidth
    ] );

    const iconMap = {
      'circle-envelope': circleEnvelopeIcon,
      'default-envelope': defaultEnvelopeIcon,
      'star-envelope': starEnvelopeIcon,
      'paper-plane': paperPlaneIcon
    };
    const altText = alt ?? '';
    const outputImageTag = ( () => {
      if ( !image || image === 'Select an icon' ) return null;

      // Check if it's one of our predefined icons
      if ( iconMap[image] ) {
        return iconMap[image];
      }

      // Otherwise it's an uploaded image URL
      return <img src={image} alt={altText} />;
    })();

    const bgColor = backgroundColor ? { backgroundColor } : undefined;
    const bColor = borderColor ? { borderColor } : undefined;

    const hColor = headingColor ? { color: headingColor } : undefined;
    const tColor = textColor ? { color: textColor } : undefined;
    const ltColor = legalTextColor ? { color: legalTextColor } : undefined;
    const efColor = emailFieldColor ? { '--email-field-color': emailFieldColor } : undefined;

    return (
      <div { ...blockProps }>
        <div
          className={ newsletterInnerClasses }
          style={ { ...bgColor, ...bColor, ...efColor } }
        >
          <div className='newsletter-wrapper mx-auto flex flex-col has-success'>

            { image && (
              <div className='newsletter__image-wrapper'>
                { outputImageTag }
              </div>
            ) }

            { heading.length > 0 && (
              <RichText.Content
                className='heading-4 newsletter__heading'
                style={ { ...hColor } }
                tagName='h4'
                allowedFormats={ [ 'core/bold', 'core/italic' ] }
                value={ heading }
              />
            ) }

            { text.length > 0 && (
              <RichText.Content
                className='paragraph-1 newsletter__text'
                style={ { ...tColor } }
                tagName='p'
                value={ text }
              />
            ) }

            {/* Block email-form */}
            <div { ...innerBlocksProps } />
            { legalText
              ? <RichText.Content
                className='paragraph-3 newsletter__legal'
                style={ { ...tColor } }
                tagName='p'
                value={ legalText }
              />
              : <LegalCopy
                className="paragraph-3 newsletter__legal"
                style={ { ...ltColor } }
              />
            }
          </div>
        </div>
      </div>
    );
  }
};

const v1 = {
  supports: {
    anchor: true
  },
  attributes: {
    backgroundColor: {
      default: '',
      type: 'string'
    },
    borderStyle: {
      default: '',
      type: 'string'
    },
    borderColor: {
      default: '',
      type: 'string'
    },
    borderWidth: {
      default: '',
      type: 'string'
    },
    image: {
      attribute: 'src',
      default: '',
      selector: 'img',
      source: 'attribute',
      type: 'string'
    },
    alt: {
      attribute: 'alt',
      default: '',
      source: 'attribute',
      type: 'string'
    },
    heading: {
      default: [],
      selector: '.newsletter__heading',
      source: 'children',
      type: 'array'
    },
    headingColor: {
      default: '',
      type: 'string'
    },
    text: {
      default: [],
      selector: '.newsletter__text',
      source: 'children',
      type: 'array'
    },
    textColor: {
      default: '',
      type: 'string'
    },
    legalText: {
      default: '',
      selector: '.newsletter__legal',
      type: 'string'
    },
    legalTextColor: {
      default: '',
      type: 'string'
    }
  },
  save({ attributes }) {
    const {
      backgroundColor,
      borderColor,
      borderStyle,
      borderWidth,
      image,
      alt,
      heading,
      headingColor,
      text,
      textColor,
      legalText,
      legalTextColor
    } = attributes;

    const blockProps = useBlockProps.save({
      className: 'block__newsletter'
    });

    const innerBlocksProps = useInnerBlocksProps.save({
      className: 'newsletter__form'
    });

    const newsletterInnerClasses = filterOutWhiteSpace( [
      'newsletter--inner',
      borderStyle,
      borderWidth
    ] );

    const altText = alt ?? '';

    const bgColor = backgroundColor ? { backgroundColor } : undefined;
    const bColor = borderColor ? { borderColor } : undefined;

    const hColor = headingColor ? { color: headingColor } : undefined;
    const tColor = textColor ? { color: textColor } : undefined;
    const ltColor = legalTextColor ? { color: legalTextColor } : undefined;

    return (
      <div { ...blockProps }>
        <div
          className={ newsletterInnerClasses }
          style={ { ...bgColor, ...bColor } }
        >
          <div className='newsletter-wrapper mx-auto flex flex-col has-success'>

            { image && (
              <div className='newsletter__image-wrapper'>
                <img src={ image } alt={ altText } />
              </div>
            ) }

            { heading.length > 0 && (
              <RichText.Content
                className='heading-4 newsletter__heading'
                style={ { ...hColor } }
                tagName='h4'
                allowedFormats={ [ 'core/bold', 'core/italic' ] }
                value={ heading }
              />
            ) }

            { text.length > 0 && (
              <RichText.Content
                className='paragraph-1 newsletter__text'
                style={ { ...tColor } }
                tagName='p'
                value={ text }
              />
            ) }

            {/* Block email-form */}
            <div { ...innerBlocksProps } />
            { legalText
              ? <RichText.Content
                className='paragraph-3 newsletter__legal'
                style={ { ...tColor } }
                tagName='p'
                value={ legalText }
              />
              : <LegalCopy
                className="paragraph-3 newsletter__legal"
                style={ { ...ltColor } }
              />
            }
          </div>
        </div>
      </div>
    );
  }
};

export default [ v3, v2, v1 ];
